import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { DateTime } from 'luxon';
import { createContext, useContext, useMemo, useState } from 'react';

import { useSearchParamsState } from '../../providers/SearchParamsStateProvider';
import { formatToISO, TimePeriod } from './utils';

export interface DateTimeRangeSnapshot {
  timePeriod: TimePeriod;
  startDate: string;
  endDate: string;
}

export type DateTimeRangeBounds = Pick<DateTimeRangeSnapshot, 'startDate' | 'endDate'>;

interface DateTimeRangeStorageContextValue {
  timePeriod: [TimePeriod, Dispatch<SetStateAction<TimePeriod>>];
  startDate: [string, Dispatch<SetStateAction<string>>];
  endDate: [string, Dispatch<SetStateAction<string>>];
  previousTimeRange: [
    DateTimeRangeSnapshot | null,
    Dispatch<SetStateAction<DateTimeRangeSnapshot | null>>,
  ];
  zoomedTimeRange: [
    DateTimeRangeBounds | null,
    Dispatch<SetStateAction<DateTimeRangeBounds | null>>,
  ];
}

const dateTimeRangeStorageContext = createContext<DateTimeRangeStorageContextValue | undefined>(
  undefined,
);

/**
 * Optional provider for `DateTimeRangePicker` and `useDateTimeRangePicker`, if they are used
 * within this provder's context then their state will be stored within.
 * Otherwise, will continue to store state in search params by default.
 */
export function DateTimeRangeStorageProvider({
  timePeriod: initialTimePeriod = TimePeriod.OneDay,
  startDate: initialStartDate = formatToISO(DateTime.now().minus({ days: 1 }).startOf('day'))!,
  endDate: initialEndDate = formatToISO(DateTime.now().endOf('day'))!,
  previousTimeRange: initialPreviousTimeRange = null,
  zoomedTimeRange: initialZoomedTimeRange = null,
  children,
}: Partial<{
  [K in keyof DateTimeRangeStorageContextValue]: DateTimeRangeStorageContextValue[K][0];
}> & {
  children: ReactNode;
}) {
  const timePeriod = useState(initialTimePeriod);
  const startDate = useState(initialStartDate);
  const endDate = useState(initialEndDate);
  const previousTimeRange = useState(initialPreviousTimeRange);
  const zoomedTimeRange = useState(initialZoomedTimeRange);

  const value = useMemo(
    (): DateTimeRangeStorageContextValue => ({
      timePeriod,
      startDate,
      endDate,
      previousTimeRange,
      zoomedTimeRange,
    }),
    [timePeriod, startDate, endDate, previousTimeRange, zoomedTimeRange],
  );

  return (
    <dateTimeRangeStorageContext.Provider value={value}>
      {children}
    </dateTimeRangeStorageContext.Provider>
  );
}

export function useDateTimeRangeValue<K extends keyof DateTimeRangeStorageContextValue>(
  key: K,
  initialValue: DateTimeRangeStorageContextValue[K][0],
): DateTimeRangeStorageContextValue[K] {
  const contextValue = useContext(dateTimeRangeStorageContext);

  const [searchParamsValue, setSearchParamsValue] = useSearchParamsState(key, initialValue);

  if (contextValue) {
    return contextValue[key];
  }

  return [searchParamsValue, setSearchParamsValue] as DateTimeRangeStorageContextValue[K];
}
